<template>
  <!-- Add Employee -->
  <div
    class="modal fade dq-modal"
    id="add_employee"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-xl dq-modal-xl">
      <div class="modal-content dq-modal-card">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div
            class="d-flex align-items-start align-items-sm-center justify-content-between w-100 gap-3"
          >
            <div class="d-flex align-items-center gap-3">
              <span class="dq-icon-badge dq-icon-employee">
                <i class="ti ti-user-plus"></i>
              </span>

              <div class="lh-1">
                <h4 class="modal-title mb-1">
                  {{ isEdit ? "Edit Employee" : "Add New Employee" }}
                </h4>

                <div class="d-flex align-items-center flex-wrap gap-2 mt-1">
                  <span class="dq-subtitle">
                    Manage employee profile & access permissions.
                  </span>

                  <span class="dq-chip">
                    <i class="ti ti-hash me-1"></i>
                    Employee ID:
                    <b
                      class="ms-1"
                      :class="{
                        'text-muted': !displayEmpId || displayEmpId === '—',
                      }"
                    >
                      {{ displayEmpId }}
                    </b>
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn dq-close-btn"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i class="ti ti-x"></i>
            </button>
          </div>
        </div>

        <form ref="employeeForm" @submit.prevent="submitForm" novalidate>
          <!-- Tabs -->
          <div class="dq-tabs-wrap">
            <ul class="nav nav-underline dq-tabs" id="myTab" role="tablist">
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link active dq-tab"
                  id="info-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#basic-info"
                  type="button"
                  role="tab"
                  aria-selected="true"
                >
                  <i class="ti ti-id-badge me-2"></i>
                  Basic Information
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link dq-tab"
                  id="address-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#address"
                  type="button"
                  role="tab"
                  aria-selected="false"
                >
                  <i class="ti ti-lock-access me-2"></i>
                  Permissions
                </button>
              </li>
            </ul>
          </div>

          <div class="tab-content dq-tab-content" id="myTabContent">
            <!-- BASIC INFO TAB -->
            <div
              class="tab-pane fade show active"
              id="basic-info"
              role="tabpanel"
              aria-labelledby="info-tab"
              tabindex="0"
            >
              <div class="modal-body dq-modal-body">
                <!-- Profile Upload Card -->
                <div class="dq-section-card mb-4">
                  <div
                    class="d-flex align-items-center justify-content-between flex-wrap gap-3"
                  >
                    <div class="d-flex align-items-center gap-3">
                      <div class="dq-avatar-wrap">
                        <div class="dq-avatar">
                          <img
                            v-if="form.imageUrl"
                            :src="form.imageUrl"
                            class="dq-avatar-img"
                            alt="Employee Avatar"
                          />
                          <i v-else class="ti ti-photo dq-avatar-icon"></i>
                        </div>
                      </div>

                      <div>
                        <h6 class="mb-1 dq-section-title">Profile photo</h6>
                        <div class="dq-help">
                          Upload a clear headshot. Recommended square image. Max
                          4MB.
                        </div>
                      </div>
                    </div>

                    <div class="d-flex align-items-center gap-2">
                      <label
                        class="btn btn-primary dq-btn-primary btn-sm dq-upload-btn mb-0"
                      >
                        <i class="ti ti-upload me-1"></i>
                        Upload
                        <input
                          type="file"
                          accept="image/*"
                          class="dq-file-input"
                          @change="onImageChange"
                        />
                      </label>

                      <button
                        v-if="form.imageUrl"
                        type="button"
                        class="btn btn-light dq-btn-soft btn-sm"
                        @click="removeImage"
                      >
                        <i class="ti ti-x me-1"></i>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>

                <div class="dq-access-callout mb-4">
                  <div class="dq-access-callout-head">
                    <span class="dq-access-role-badge">
                      <i class="ti ti-shield-check me-1"></i>
                      {{ EMPLOYEE_ROLE_NAME }} role
                      <span v-if="!isEdit" class="dq-access-role-tag">default</span>
                    </span>
                  </div>
                  <p class="dq-access-callout-text mb-2">
                    <template v-if="!isEdit">
                      The
                      <strong>{{ EMPLOYEE_ROLE_NAME }}</strong> role is applied by
                      default — attendance, timesheets, and leave self-service.
                      Use Permissions only if they need extra access.
                    </template>
                    <template v-else>
                      Standard employees use the
                      <strong>{{ EMPLOYEE_ROLE_NAME }}</strong> role. Effective access
                      includes the defaults below, plus any extras set on the
                      Permissions tab.
                    </template>
                  </p>
                  <div class="dq-role-defaults">
                    <div
                      v-for="group in employeeRoleDefaultGroups"
                      :key="group.module"
                      class="dq-role-default-row"
                    >
                      <span class="dq-role-default-module">{{ group.moduleLabel }}</span>
                      <span class="dq-role-default-actions">
                        <span
                          v-for="action in group.actions"
                          :key="`${group.module}.${action.key}`"
                          class="dq-role-default-chip"
                        >
                          {{ action.label }}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Form Grid -->
                <div class="row">
                  <div class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        First Name <span class="text-danger">*</span>
                      </label>
                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-user"></i
                        ></span>
                        <input
                          v-model="form.first_name"
                          type="text"
                          class="form-control dq-input"
                          required
                          placeholder="e.g. Jane"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">Middle Name</label>
                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-user"></i
                        ></span>
                        <input
                          v-model="form.middle_name"
                          type="text"
                          class="form-control dq-input"
                          placeholder="e.g. Brooks"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">Last Name</label>
                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-user"></i
                        ></span>
                        <input
                          v-model="form.last_name"
                          type="text"
                          class="form-control dq-input"
                          placeholder="e.g. Doe"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        Joining Date <span class="text-danger">*</span>
                      </label>
                      <div class="dq-date-wrap">
                        <date-picker
                          v-model="form.company_doj"
                          class="form-control dq-date"
                          placeholder="dd/mm/yyyy"
                          :editable="true"
                          :clearable="false"
                          :input-format="dateFormat"
                        />
                        <span class="dq-date-icon">
                          <i class="ti ti-calendar"></i>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        Date of Birth <span class="text-danger">*</span>
                      </label>
                      <div class="dq-date-wrap">
                        <date-picker
                          v-model="form.dob"
                          class="form-control dq-date"
                          placeholder="dd/mm/yyyy"
                          :editable="true"
                          :clearable="false"
                          :input-format="dateFormat"
                        />
                        <span class="dq-date-icon">
                          <i class="ti ti-calendar"></i>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        Employee ID <span class="text-danger">*</span>
                      </label>

                      <div
                        class="d-flex align-items-center justify-content-between mb-2"
                      >
                        <div class="dq-help mb-0">
                          Choose system-generated or enter a custom ID.
                        </div>

                        <label class="dq-switch mb-0">
                          <input
                            type="checkbox"
                            v-model="useSystemEmployeeId"
                          />
                          <span class="dq-slider"></span>
                          <span class="dq-switch-label">System</span>
                        </label>
                      </div>

                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-hash"></i
                        ></span>
                        <input
                          v-model="form.employee_id"
                          type="text"
                          class="form-control dq-input"
                          :readonly="useSystemEmployeeId"
                          :placeholder="
                            useSystemEmployeeId
                              ? 'System ID'
                              : 'Enter employee ID'
                          "
                          required
                        />
                      </div>

                      <div class="dq-help" v-if="useSystemEmployeeId">
                        Auto-filled from the next system ID.
                      </div>
                      <div class="dq-help" v-else>Must be unique.</div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        Username <span class="text-danger">*</span>
                      </label>

                      <div
                        class="d-flex align-items-center justify-content-between mb-2"
                      >
                        <div class="dq-help mb-0">
                          System format:
                          <b>{{ suggestedUsername || "first.last" }}</b>
                        </div>

                        <label class="dq-switch mb-0">
                          <input type="checkbox" v-model="useSystemUsername" />
                          <span class="dq-slider"></span>
                          <span class="dq-switch-label">System</span>
                        </label>
                      </div>

                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-at"></i
                        ></span>
                        <input
                          v-model="form.username"
                          type="text"
                          class="form-control dq-input"
                          required
                          :readonly="useSystemUsername"
                          :placeholder="
                            useSystemUsername
                              ? 'Auto username'
                              : 'Enter username'
                          "
                        />
                      </div>

                      <div class="dq-help" v-if="useSystemUsername">
                        Auto-updates as you type first/last name.
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        Email <span class="text-danger">*</span>
                      </label>
                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-mail"></i
                        ></span>
                        <input
                          v-model="form.email"
                          type="email"
                          class="form-control dq-input"
                          required
                          placeholder="e.g. jane.doe@company.com"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        Gender <span class="text-danger">*</span>
                      </label>
                      <div class="dq-select-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-gender-bigender"></i
                        ></span>
                        <vue-select
                          v-model="form.gender"
                          :options="['Male', 'Female', 'Other']"
                          placeholder="Select Gender"
                          class="dq-vselect"
                          :clearable="false"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        Disability Status <span class="text-danger">*</span>
                      </label>

                      <div class="dq-select-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-accessible"></i
                        ></span>
                        <vue-select
                          v-model="form.disability_status"
                          :options="['None', 'PWD', 'PreferNotToSay']"
                          placeholder="Select Status"
                          class="dq-vselect"
                          :clearable="false"
                        />
                      </div>

                      <div class="dq-help">PWD = Person With Disability.</div>
                      <div class="dq-help">
                        PAYE exemption applies only if the employee has a valid
                        verified KRA/NCPWD exemption certificate.
                      </div>
                    </div>
                  </div>

                  <!-- Show extra fields only if PWD -->
                  <div v-if="form.disability_status === 'PWD'" class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">Disability Type</label>
                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-clipboard-text"></i
                        ></span>
                        <input
                          v-model="form.disability_type"
                          type="text"
                          class="form-control dq-input"
                          placeholder="e.g. Visual impairment"
                        />
                      </div>
                    </div>
                  </div>

                  <div v-if="form.disability_status === 'PWD'" class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label"
                        >PWD Registration No.</label
                      >
                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-id-badge"></i
                        ></span>
                        <input
                          v-model="form.disability_registration_no"
                          type="text"
                          class="form-control dq-input"
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                  </div>

                  <div v-if="form.disability_status === 'PWD'" class="col-12">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">Notes</label>
                      <textarea
                        v-model="form.disability_notes"
                        class="form-control dq-input"
                        rows="2"
                        placeholder="Optional notes (reasonable accommodations, etc.)"
                      ></textarea>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        Tax Payer PIN <span class="text-danger">*</span>
                      </label>
                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-file-text"></i
                        ></span>
                        <input
                          v-model="form.kra_pin"
                          type="text"
                          class="form-control dq-input"
                          required
                          placeholder="e.g. A012345678Z"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        National ID <span class="text-danger">*</span>
                      </label>
                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-id"></i
                        ></span>
                        <input
                          v-model="form.national_id"
                          type="text"
                          class="form-control dq-input"
                          required
                          placeholder="e.g. 12345678"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        Phone Number <span class="text-danger">*</span>
                      </label>
                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-phone"></i
                        ></span>
                        <input
                          v-model="form.phone"
                          type="text"
                          class="form-control dq-input"
                          required
                          placeholder="e.g. +2547XXXXXXXX"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6 display-none">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        Company <span class="text-danger">*</span>
                      </label>
                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-building"></i
                        ></span>
                        <input
                          type="text"
                          class="form-control dq-input"
                          :value="companyLabel"
                          disabled
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">Department</label>
                      <div class="dq-select-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-building-community"></i
                        ></span>
                        <vue-select
                          v-model="form.department_id"
                          :options="departments"
                          label="name"
                          track-by="id"
                          placeholder="Select Department"
                          class="dq-vselect"
                          :clearable="true"
                          :reduce="(o) => o.id"
                        />
                      </div>
                      <!-- <div class="dq-help">
                        Selecting a department filters the designation list.
                      </div> -->
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">Section</label>
                      <div class="dq-select-wrap">
                        <span class="dq-input-icon"><i class="ti ti-layout-grid"></i></span>
                        <vue-select
                          v-model="form.section_id"
                          :options="filteredSections"
                          label="name"
                          track-by="id"
                          :disabled="!form.department_id"
                          :placeholder="
                            form.department_id
                              ? 'Select Section (optional)'
                              : 'Select Department first'
                          "
                          class="dq-vselect"
                          :clearable="true"
                          :reduce="(o) => o.id"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">Designation</label>
                      <div class="dq-select-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-award"></i
                        ></span>
                        <vue-select
                          v-model="form.designation_id"
                          :options="filteredDesignations"
                          label="name"
                          track-by="id"
                          :disabled="!form.department_id"
                          :placeholder="
                            form.department_id
                              ? 'Select Designation'
                              : 'Select Department first'
                          "
                          class="dq-vselect"
                          :clearable="true"
                          :reduce="(o) => o.id"
                        />
                      </div>
                      <!-- <div class="dq-help" v-if="!form.department_id">
                        Choose a department to unlock designation selection.
                      </div> -->
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">
                        Nature of Employment <span class="text-danger">*</span>
                      </label>
                      <div class="dq-select-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-briefcase"></i
                        ></span>
                        <vue-select
                          v-model="form.employment_type"
                          :options="[
                            'Fulltime',
                            'Parttime',
                            'Contract',
                            'Probation',
                            'Internship',
                            'WFH',
                          ]"
                          placeholder="Select Employment Type"
                          class="dq-vselect"
                          :clearable="false"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label">Pay Frequency</label>
                      <div class="dq-select-wrap">
                        <span class="dq-input-icon"><i class="ti ti-calendar-repeat"></i></span>
                        <vue-select
                          v-model="form.pay_frequency"
                          :options="['monthly', 'weekly', 'fortnightly', 'daily']"
                          placeholder="Select Pay Frequency"
                          class="dq-vselect"
                          :clearable="false"
                        />
                      </div>
                      <div class="dq-help">Defines which payroll run cycle this employee belongs to (monthly, weekly, fortnightly, or daily).</div>
                    </div>
                  </div>

                  <div v-if="form.disability_status === 'PWD'" class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label"
                        >PWD Tax Exemption Cert No.</label
                      >
                      <div class="dq-input-wrap">
                        <span class="dq-input-icon"
                          ><i class="ti ti-certificate"></i
                        ></span>
                        <input
                          v-model="form.pwd_tax_exemption_cert_no"
                          type="text"
                          class="form-control dq-input"
                          placeholder="KRA/NCPWD certificate number"
                        />
                      </div>
                    </div>
                  </div>

                  <div v-if="form.disability_status === 'PWD'" class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label"
                        >Exemption Start Date</label
                      >
                      <input
                        v-model="form.pwd_tax_exemption_start_date"
                        type="date"
                        class="form-control dq-input"
                      />
                    </div>
                  </div>

                  <div v-if="form.disability_status === 'PWD'" class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label"
                        >Exemption End Date</label
                      >
                      <input
                        v-model="form.pwd_tax_exemption_end_date"
                        type="date"
                        class="form-control dq-input"
                      />
                    </div>
                  </div>

                  <div v-if="form.disability_status === 'PWD'" class="col-md-4">
                    <div class="mb-3 dq-field">
                      <label class="form-label dq-label d-block"
                        >PWD Tax Exemption Verified</label
                      >
                      <div class="form-check form-switch mt-2">
                        <input
                          v-model="form.pwd_tax_exemption_verified"
                          class="form-check-input"
                          type="checkbox"
                        />
                        <label class="form-check-label">
                          Enable only after confirming a valid certificate.
                        </label>
                      </div>
                    </div>
                  </div>

                  <div class="col-12">
                    <div
                      v-if="isEdit && hasLinkedUser"
                      class="dq-inline-setting"
                    >
                      <div>
                        <div class="dq-inline-setting-title">
                          Portal login is already linked
                        </div>
                        <div class="dq-help mb-0">
                          This employee is linked to a user account. Resend the
                          set-password email if they never received it or need a
                          new link.
                        </div>
                      </div>

                      <button
                        type="button"
                        class="btn btn-outline-primary btn-sm"
                        :disabled="resendingSetPassword || usesPlaceholderEmail"
                        @click="resendSetPasswordEmail"
                      >
                        <span
                          v-if="resendingSetPassword"
                          class="spinner-border spinner-border-sm me-1"
                        ></span>
                        Resend set-password email
                      </button>
                    </div>

                    <div
                      v-if="isEdit && !hasLinkedUser"
                      class="dq-inline-setting"
                    >
                      <div>
                        <div class="dq-inline-setting-title">
                          Create login access for this employee
                        </div>
                        <div class="dq-help mb-0">
                          <span v-if="usesPlaceholderEmail">
                            Replace the imported placeholder email first. Login
                            access cannot be created with a placeholder email.
                          </span>
                          <span v-else>
                            If enabled, the system will create a linked employee
                            user, mark the email as already verified, and use
                            the welcome email to deliver access details.
                          </span>
                        </div>
                      </div>

                      <label class="dq-switch mb-0">
                        <input
                          type="checkbox"
                          v-model="form.create_login_user"
                          :disabled="usesPlaceholderEmail"
                        />
                        <span class="dq-slider"></span>
                        <span class="dq-switch-label">
                          {{ form.create_login_user ? "Yes" : "No" }}
                        </span>
                      </label>
                    </div>

                    <div
                      v-if="isEdit && !hasLinkedUser && form.create_login_user"
                      class="dq-inline-setting"
                    >
                      <div>
                        <div class="dq-inline-setting-title">
                          Send login details to the employee
                        </div>
                        <div class="dq-help mb-0">
                          Recommended when provisioning first-time access for an
                          imported employee.
                        </div>
                      </div>

                      <label class="dq-switch mb-0">
                        <input
                          type="checkbox"
                          v-model="form.send_welcome_email"
                        />
                        <span class="dq-slider"></span>
                        <span class="dq-switch-label">
                          {{ form.send_welcome_email ? "Yes" : "No" }}
                        </span>
                      </label>
                    </div>

                    <div
                      v-if="!isEdit"
                      class="dq-inline-setting"
                    >
                      <div>
                        <div class="dq-inline-setting-title">
                          Send a welcome email to this employee
                        </div>
                        <div class="dq-help mb-0">
                          If enabled, the employee will receive login and onboarding details after this employee is created.
                        </div>
                      </div>

                      <label class="dq-switch mb-0">
                        <input
                          type="checkbox"
                          v-model="form.send_welcome_email"
                        />
                        <span class="dq-slider"></span>
                        <span class="dq-switch-label">
                          {{ form.send_welcome_email ? "Yes" : "No" }}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Sticky footer -->
              <div class="modal-footer dq-modal-footer">
                <button
                  type="button"
                  class="btn btn-outline-light border dq-btn-soft me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="btn btn-primary dq-btn-primary"
                  :disabled="submitting"
                >
                  <span
                    v-if="submitting"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  <span v-if="submitting">Saving…</span>
                  <span v-else>Save</span>
                  <i class="ti ti-device-floppy ms-2"></i>
                </button>
              </div>
            </div>

            <!-- PERMISSIONS TAB -->
            <div
              class="tab-pane fade"
              id="address"
              role="tabpanel"
              aria-labelledby="address-tab"
              tabindex="0"
            >
              <div class="modal-body dq-modal-body">
                <div class="dq-perm-top">
                  <div class="dq-perm-title">
                    <div class="dq-perm-badge">
                      <i class="ti ti-lock"></i>
                    </div>
                    <div>
                      <h6 class="mb-0">Extra permissions (optional)</h6>
                      <div class="dq-help">
                        <template v-if="!isEdit">
                          Leave this tab empty for standard employees. The
                          {{ EMPLOYEE_ROLE_NAME }} role already covers attendance,
                          timesheets, and leave self-service.
                        </template>
                        <template v-else>
                          Checked items show effective access (role defaults plus
                          any direct overrides). Unchecking only removes direct
                          overrides — role defaults remain.
                        </template>
                      </div>
                    </div>
                  </div>

                  <div class="dq-perm-actions">
                    <label class="dq-switch">
                      <input
                        type="checkbox"
                        v-model="allPermissions"
                        @change="toggleAllPermissions"
                      />
                      <span class="dq-slider"></span>
                      <span class="dq-switch-label">Enable all modules</span>
                    </label>

                    <label class="dq-switch">
                      <input
                        type="checkbox"
                        v-model="selectAllPermissions"
                        @change="toggleSelectAll"
                      />
                      <span class="dq-slider"></span>
                      <span class="dq-switch-label">Select all actions</span>
                    </label>
                  </div>
                </div>

                <div class="dq-access-callout dq-access-callout-compact mb-3">
                  <div class="dq-access-callout-head">
                    <span class="dq-access-role-badge">
                      <i class="ti ti-shield-check me-1"></i>
                      Included via {{ EMPLOYEE_ROLE_NAME }} role
                    </span>
                  </div>
                  <p class="dq-access-callout-text mb-2">
                    <template v-if="!isEdit">
                      No checkboxes need to be selected for a normal hire. Use the
                      matrix below only when you need access beyond these defaults.
                    </template>
                    <template v-else>
                      These defaults apply through the employee's role even if
                      unchecked in the matrix below.
                    </template>
                  </p>
                  <div class="dq-role-defaults">
                    <div
                      v-for="group in employeeRoleDefaultGroups"
                      :key="`perm-${group.module}`"
                      class="dq-role-default-row"
                    >
                      <span class="dq-role-default-module">{{ group.moduleLabel }}</span>
                      <span class="dq-role-default-actions">
                        <span
                          v-for="action in group.actions"
                          :key="`perm-${group.module}.${action.key}`"
                          class="dq-role-default-chip"
                        >
                          {{ action.label }}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <div class="dq-perm-table-wrap">
                  <div class="dq-perm-section-label">
                    Additional permissions beyond the {{ EMPLOYEE_ROLE_NAME }} role
                  </div>
                  <div class="table-responsive">
                    <table class="table dq-perm-table mb-0">
                      <tbody>
                        <tr v-for="perm in permissionsList" :key="perm.module">
                          <td class="dq-perm-module">
                            <label class="dq-checkline">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="perm.enabled"
                                @change="syncToggles"
                              />
                              <span class="dq-checkline-text">{{
                                perm.module
                              }}</span>
                            </label>
                          </td>

                          <td
                            v-for="action in perm.actions"
                            :key="action.name"
                            class="dq-perm-cell"
                          >
                            <label class="dq-checkline dq-checkline-action">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="action.value"
                                @change="syncToggles"
                                :disabled="!perm.enabled"
                              />
                              <span class="dq-checkline-text">{{
                                action.name
                              }}</span>
                            </label>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div class="dq-perm-footnote">
                    <i class="ti ti-info-circle me-2"></i>
                    Actions are only applied when the module is enabled.
                  </div>
                </div>
              </div>

              <div class="modal-footer dq-modal-footer">
                <button
                  type="button"
                  class="btn btn-outline-light border dq-btn-soft me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="btn btn-primary dq-btn-primary"
                  :disabled="submitting"
                  formnovalidate
                >
                  <span
                    v-if="submitting"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  <span v-if="submitting">Saving…</span>
                  <span v-else>Save</span>
                  <i class="ti ti-device-floppy ms-2"></i>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
  <!-- /Add Employee -->

  <!-- Delete Modal -->
  <div
    class="modal fade dq-modal"
    id="delete_modal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-card">
        <div class="modal-body dq-delete-body">
          <div class="dq-danger-hero">
            <span class="dq-danger-badge">
              <i class="ti ti-trash-x"></i>
            </span>
          </div>

          <h4 class="mb-1 dq-delete-title">Confirm delete</h4>
          <p class="mb-3 dq-delete-subtitle">
            Are you sure you want to delete
            <b
              >{{ employeeToDelete?.first_name }}
              {{ employeeToDelete?.last_name }}</b
            >? This action cannot be undone.
          </p>

          <div class="dq-delete-warning">
            <i class="ti ti-alert-triangle me-2"></i>
            Tip: If you want to stop access, consider disabling the account
            instead of deleting.
          </div>

          <div class="d-flex justify-content-center gap-2 mt-4">
            <button
              type="button"
              class="btn btn-light dq-btn-soft"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <a
              href="javascript:void(0);"
              @click="confirmDelete"
              class="btn btn-danger dq-btn-danger"
            >
              Yes, Delete
              <i class="ti ti-arrow-right ms-2"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- /Delete Modal -->
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted } from "vue";
import moment from "moment";
import { useAuthStore } from "@/stores/authStore";
import { storeToRefs } from "pinia";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useDepartmentStore } from "@/stores/departmentStore";
import { useDesignationStore } from "@/stores/designationStore";
import { useSectionStore } from "@/stores/sectionStore";
import { useCompanyStore } from "@/stores/companyStore";
import { useCatalogStore } from "@/stores/catalogStore";
import {
  EMPLOYEE_ROLE_NAME,
  groupEmployeeRoleDefaults,
} from "@/utils/employeeRoleDefaults";

import { toast } from "@/utils/toast";
import employeeService from "@/services/hrm/employeeService";

// Props & Emits
const props = defineProps({
  employee: { type: Object, default: null }, // null for add, object for edit
  employeeId: String, // new id for add mode
  employeeToDelete: { type: Object, default: null },
});
const emit = defineEmits(["refresh"]);
const employeeForm = ref(null);
const resendingSetPassword = ref(false);

// Stores
const employeeStore = useEmployeeStore();
const departmentStore = useDepartmentStore();
const designationStore = useDesignationStore();
const sectionStore = useSectionStore();
const companyStore = useCompanyStore();
const catalogStore = useCatalogStore();

const permissionsList = reactive([]);

/** Build UI model from catalog: [{module, enabled, actions:[{name,value}]}] */
function buildPermissionsFromCatalog() {
  permissionsList.splice(0, permissionsList.length);

  const mods = catalogStore.modules || [];
  const acts = catalogStore.actions || [];

  mods.forEach((m) => {
    permissionsList.push({
      module: m.key, // used in permission string
      label: m.label || m.key,
      group: m.group || null,
      icon: m.icon || null,
      enabled: false,
      actions: acts.map((a) => ({ name: a.key, value: false })),
    });
  });

  // reset toggles after rebuild
  allPermissions.value = false;
  selectAllPermissions.value = false;
}

async function ensureCatalogReady() {
  if (!catalogStore.loaded) await catalogStore.fetchPermissionsCatalog();
  if (!permissionsList.length) buildPermissionsFromCatalog();
}

const authStore = useAuthStore();
const { user: authUser } = storeToRefs(authStore);
const isSuperAdmin = computed(() => {
  const roles = (authUser.value?.roles || []).map((r) =>
    typeof r === "string" ? r : r.name,
  );
  return roles.includes("SuperAdmin");
});

const { activeCompanyId } = storeToRefs(companyStore);

const effectiveCompanyId = computed(() =>
  isSuperAdmin.value ? form.company_id || null : activeCompanyId.value,
);

// Helper for modal header
const isEdit = computed(() => !!(props.employee && props.employee.id));
const employeeRoleDefaultGroups = computed(() =>
  groupEmployeeRoleDefaults(catalogStore),
);
const displayEmpId = computed(() => {
  // EDIT MODE → always reflect current value
  if (isEdit.value) {
    return form.employee_id || "—";
  }

  // ADD MODE
  if (useSystemEmployeeId.value) {
    return props.employeeId || "—";
  }

  // ADD MODE + custom ID
  return form.employee_id || "—";
});

function toIsoDate(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const da = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

// Form
function defaultForm() {
  return {
    avatar: null,
    imageUrl: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    employee_id: "",
    company_doj: null,
    dob: null,
    username: "",
    email: "",
    gender: "",
    disability_status: "None",
    disability_type: "",
    disability_registration_no: "",
    disability_notes: "",
    pwd_tax_exemption_cert_no: "",
    pwd_tax_exemption_start_date: "",
    pwd_tax_exemption_end_date: "",
    pwd_tax_exemption_verified: false,

    password: "",
    password_confirmation: "",
    phone: "",
    company: "",
    company_id: "",
    department_id: null,
    section_id: null,
    designation_id: null,
    kra_pin: "",
    national_id: "",
    nssf_number: "",
    nhif_number: "",
    about: "",
    employment_type: "",
    pay_frequency: "monthly",
    create_login_user: false,
    send_welcome_email: true,
    permissions: [],
  };
}
const form = reactive(defaultForm());
const submitting = ref(false);
const useSystemEmployeeId = ref(true);
const useSystemUsername = ref(true);

// Patch form when editing, reset when adding
watch(
  () => [props.employee, props.employeeId],
  ([emp, empId]) => {
    Object.assign(form, defaultForm());
    permissionsList.forEach((perm) => {
      perm.enabled = false;
      perm.actions.forEach((a) => (a.value = false));
    });

    if (emp) {
      form.employee_id = emp.employee_id || "";
      form.first_name = emp.first_name || "";
      form.middle_name = emp.middle_name || "";
      form.last_name = emp.last_name || "";
      form.email = emp.email || "";
      form.username = emp.username || "";
      form.phone = emp.phone || "";
      form.gender = emp.gender || "";
      form.disability_status = emp.disability_status || "None";
      form.disability_type = emp.disability_type || "";
      form.disability_registration_no = emp.disability_registration_no || "";
      form.disability_notes = emp.disability_notes || "";
      form.pwd_tax_exemption_cert_no = emp.pwd_tax_exemption_cert_no || "";
      form.pwd_tax_exemption_start_date =
        emp.pwd_tax_exemption_start_date || "";
      form.pwd_tax_exemption_end_date = emp.pwd_tax_exemption_end_date || "";
      form.pwd_tax_exemption_verified = !!emp.pwd_tax_exemption_verified;
      form.company = emp.company || "";
      form.company_id = emp.company_id || "";
      form.company_doj = emp.company_doj ? new Date(emp.company_doj) : null;
      form.dob = emp.dob ? new Date(emp.dob) : null;
      form.department_id = emp.department_id ? Number(emp.department_id) : null;
      form.section_id = emp.section_id ? Number(emp.section_id) : null;
      form.designation_id = emp.designation_id
        ? Number(emp.designation_id)
        : null;

      form.about = emp.about || "";
      form.employment_type = emp.employment_type || "";
      form.pay_frequency = emp.pay_frequency || "monthly";
      form.imageUrl = emp.avatar_url || emp.imageUrl || "";
      form.kra_pin = emp.kra_pin || "";
      form.national_id = emp.national_id || "";
      form.nssf_number = emp.nssf_number || "";
      form.nhif_number = emp.nhif_number || "";
      form.create_login_user = false;
      form.send_welcome_email = false;

      // if (Array.isArray(emp?.user?.permissions)) {
      //   emp?.user?.permissions.forEach((p) => {
      //     const name = typeof p === "object" ? p.name : p;
      //     if (!name.includes(".")) return;
      //     const [mod, act] = name.split(".");
      //     const modObj = permissionsList.find((x) => x.module === mod);
      //     if (modObj) {
      //       modObj.enabled = true;
      //       const actObj = modObj.actions.find((a) => a.name === act);
      //       if (actObj) actObj.value = true;
      //     }
      //   });
      // }

      if (Array.isArray(emp?.user?.permissions)) {
        emp.user.permissions.forEach((p) => {
          const raw = typeof p === "object" ? p.name : p;
          const name = catalogStore.normalizePermissionName(raw);

          if (!name.includes(".")) return;
          const [mod, act] = name.split(".");

          const modObj = permissionsList.find((x) => x.module === mod);
          if (modObj) {
            modObj.enabled = true;
            const actObj = modObj.actions.find((a) => a.name === act);
            if (actObj) actObj.value = true;
          }
        });

        syncToggles();
      }
    } else {
      form.employee_id = empId || "";
      useSystemEmployeeId.value = true;
      useSystemUsername.value = true;
      form.username = "";
      form.send_welcome_email = true;
    }
  },
  { immediate: true },
);

// Permissions: collect selected
function getSelectedPermissions() {
  const sel = [];
  permissionsList.forEach((m) => {
    if (!m.enabled) return;
    m.actions.forEach((a) => {
      if (a.value) sel.push(`${m.module}.${a.name}`);
    });
  });
  return sel;
}

// function getSelectedPermissions() {
//   const sel = [];
//   permissionsList.forEach((m) => {
//     if (!m.enabled) return;
//     m.actions.forEach((a) => {
//       if (a.value) sel.push(`${m.module}.${a.name}`);
//     });
//   });
//   return sel;
// }

// Image preview & removal
function onImageChange(e) {
  const file = e.target.files[0];
  if (!file) return;
  form.avatar = file;
  const reader = new FileReader();
  reader.onload = (evt) => {
    form.imageUrl = evt.target.result;
  };
  reader.readAsDataURL(file);
}
function removeImage() {
  form.avatar = null;
  form.imageUrl = "";
}

// Passwords
const showPassword = ref(false);
const showPassword1 = ref(false);
function toggleShow() {
  showPassword.value = !showPassword.value;
}
function toggleShow1() {
  showPassword1.value = !showPassword1.value;
}
const passwordsMatch = computed(
  () =>
    form.password_confirmation === "" ||
    form.password === form.password_confirmation,
);

// Departments & Designations
const departments = computed(() =>
  departmentStore.departments.map((d) => ({ id: d.id, name: d.name, text: d.name })),
);

const allSections = computed(() =>
  sectionStore.sections.map((s) => ({
    id: s.id,
    name: s.name,
    text: s.name,
    department_id: s.department_id,
  })),
);

const allDesignations = computed(() =>
  designationStore.designations.map((d) => ({
    id: d.id,
    name: d.name,
    text: d.name,
    department_id: d.department_id,
    section_id: d.section_id,
  })),
);

watch(
  () => form.department_id,
  () => {
    if (!filteredSections.value.some((s) => s.id === form.section_id)) {
      form.section_id = null;
    }
    if (!filteredDesignations.value.some((d) => d.id === form.designation_id)) {
      form.designation_id = null;
    }
  },
);

watch(
  () => form.section_id,
  () => {
    if (!filteredDesignations.value.some((d) => d.id === form.designation_id)) {
      form.designation_id = null;
    }
  },
);

const filteredSections = computed(() => {
  const depId =
    form.department_id == null || form.department_id === ""
      ? null
      : Number(form.department_id);
  if (!depId) return [];
  return allSections.value.filter((s) => Number(s.department_id) === depId);
});

const filteredDesignations = computed(() => {
  const depId =
    form.department_id == null || form.department_id === ""
      ? null
      : Number(form.department_id);
  if (!depId) return [];

  const secId =
    form.section_id == null || form.section_id === ""
      ? null
      : Number(form.section_id);

  return allDesignations.value.filter((d) => {
    if (Number(d.department_id) !== depId) return false;
    if (!secId) return true;
    // When a section is chosen: show designations for that section, plus
    // department-level designations with no section (legacy / shared titles).
    return !d.section_id || Number(d.section_id) === secId;
  });
});

function slugUsername(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ".")
    .replace(/[^a-z0-9._-]/g, "")
    .replace(/\.+/g, ".")
    .replace(/^\.|\.$/g, "");
}

const suggestedUsername = computed(() => {
  const first = slugUsername(form.first_name);
  const last = slugUsername(form.last_name);
  const base = [first, last].filter(Boolean).join(".");
  return base;
});

const companies = computed(() =>
  companyStore.companies.map((c) => ({ id: c.id, text: c.company_name })),
);

// Permissions toggles
const allPermissions = ref(false);
const selectAllPermissions = ref(false);
const IMPORT_PLACEHOLDER_EMAIL_DOMAIN = "@import.donequick.local";

const hasLinkedUser = computed(
  () => !!(props.employee?.user?.id || props.employee?.user_id),
);

const usesPlaceholderEmail = computed(() =>
  String(form.email || "")
    .trim()
    .toLowerCase()
    .endsWith(IMPORT_PLACEHOLDER_EMAIL_DOMAIN),
);

watch(usesPlaceholderEmail, (isPlaceholder) => {
  if (isPlaceholder) {
    form.create_login_user = false;
  }
});

async function resendSetPasswordEmail() {
  const id = props.employee?.id;
  if (!id) return;
  resendingSetPassword.value = true;
  try {
    const { data } = await employeeService.resendSetPasswordEmail(id);
    toast(data?.message || "Set-password email sent.", "success");
  } catch (e) {
    toast(
      e?.response?.data?.message || "Failed to send set-password email.",
      "error",
    );
  } finally {
    resendingSetPassword.value = false;
  }
}

function toggleAllPermissions() {
  const enable = allPermissions.value;
  permissionsList.forEach((perm) => {
    perm.enabled = enable;
    perm.actions.forEach((a) => (a.value = enable));
  });
  selectAllPermissions.value = enable;
  syncToggles();
}
function toggleSelectAll() {
  const select = selectAllPermissions.value;
  permissionsList.forEach((perm) => {
    perm.enabled = select;
    perm.actions.forEach((a) => (a.value = select));
  });
  syncToggles();
}
function syncToggles() {
  selectAllPermissions.value = permissionsList.every((perm) =>
    perm.actions.every((a) => a.value),
  );
  allPermissions.value = permissionsList.every(
    (perm) => perm.enabled && perm.actions.every((a) => a.value),
  );
}

// Form submit
async function submitForm() {
  if (employeeForm.value && !employeeForm.value.checkValidity()) {
    document.getElementById("info-tab")?.click();
    employeeForm.value.reportValidity();
    return;
  }
  if (!passwordsMatch.value) {
    toast("Passwords do not match.", "error");
    return;
  }
  if (isSuperAdmin.value && !effectiveCompanyId.value) {
    toast("Please select a company for this employee.", "error");
    return;
  }

  if (!isSuperAdmin.value && !effectiveCompanyId.value) {
    toast(
      "No active company selected. Please re-fresh browser or re-login.",
      "error",
    );
    return;
  }

  submitting.value = true;
  form.permissions = getSelectedPermissions();
  const fd = new FormData();

  if (effectiveCompanyId.value) {
    fd.set("company_id", String(effectiveCompanyId.value));
  }

  for (let [k, v] of Object.entries(form)) {
    if (k === "permissions") {
      v.forEach((p) => fd.append("permissions[]", p));
    } else if (k === "pwd_tax_exemption_verified") {
      fd.append("pwd_tax_exemption_verified", v ? "1" : "0");
    } else if (k === "create_login_user") {
      fd.append("create_login_user", v ? "1" : "0");
    } else if (k === "send_welcome_email") {
      fd.append("send_welcome_email", v ? "1" : "0");
    } else if (k === "company_doj" && v) {
      fd.append("company_doj", moment(v).format("YYYY-MM-DD"));
    } else if (k === "dob" && v) {
      fd.append("dob", moment(v).format("YYYY-MM-DD"));
    } else if (k === "avatar" && v instanceof File) {
      fd.append("avatar", v);
    } else if (v !== null && v !== "") {
      fd.append(k, v);
    }
  }
  try {
    if (isEdit.value) {
      fd.append("_method", "PUT");
      if (!form.password) {
        fd.delete("password");
        fd.delete("password_confirmation");
      }
      await employeeStore.updateEmployee(
        props.employee.id,
        fd,
        effectiveCompanyId.value,
      );
    } else {
      await employeeStore.createEmployee(fd, effectiveCompanyId.value);
    }
    emit("refresh");
    setTimeout(() => closeModal("add_employee"), 100);
  } catch (err) {
    // handled elsewhere
  } finally {
    submitting.value = false;
  }
}

function closeModal(id) {
  const modalEl = document.getElementById(id);
  if (modalEl) {
    modalEl.classList.remove("show");
    modalEl.style.display = "none";
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.removeAttribute("aria-modal");
  }
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

// Delete
async function confirmDelete() {
  if (props.employeeToDelete && props.employeeToDelete.id) {
    await employeeStore.deleteEmployee(props.employeeToDelete.id);
    emit("refresh");
    closeModal("delete_modal");
  }
}

// keep form in sync if not super admin
watch(
  () => [isSuperAdmin.value, activeCompanyId.value],
  () => {
    if (!isSuperAdmin.value) {
      form.company_id = activeCompanyId.value || "";
    }
  },
  { immediate: true },
);

const companyLabel = computed(() => {
  const c = companyStore.companies.find((c) => c.id === activeCompanyId.value);
  return c ? c.company_name : "";
});

// Keep Employee ID synced when "System" is on
watch(
  () => [useSystemEmployeeId.value, props.employeeId, isEdit.value],
  () => {
    if (!isEdit.value && useSystemEmployeeId.value) {
      form.employee_id = props.employeeId || "";
    }
  },
  { immediate: true },
);

// Keep Username synced when "System" is on
watch(
  () => [useSystemUsername.value, form.first_name, form.last_name],
  () => {
    if (useSystemUsername.value) {
      form.username = suggestedUsername.value || "";
    }
  },
  { immediate: true },
);

// If user flips system toggles ON, instantly apply system value
watch(useSystemEmployeeId, (v) => {
  if (!isEdit.value && v) form.employee_id = props.employeeId || "";
});

watch(useSystemUsername, (v) => {
  if (v) form.username = suggestedUsername.value || "";
});

watch(
  () => form.disability_status,
  (v) => {
    if (v !== "PWD") {
      form.disability_type = "";
      form.disability_registration_no = "";
      form.disability_notes = "";
      form.pwd_tax_exemption_cert_no = "";
      form.pwd_tax_exemption_start_date = "";
      form.pwd_tax_exemption_end_date = "";
      form.pwd_tax_exemption_verified = false;
    }
  },
);

// Fetch departments/designations on mount
onMounted(async () => {
  if (!departmentStore.departments.length) await departmentStore.ensureDepartments();
  if (!sectionStore.sections.length) await sectionStore.ensureSections();
  if (!designationStore.designations.length)
    await designationStore.ensureDesignations();
  if (!companyStore.companies.length) companyStore.fetchCompanies();

  await ensureCatalogReady();
});
</script>

<style scoped>
/* ===== Premium modal polish (UI only) ===== */
.dq-modal-card {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 22px 60px rgba(16, 24, 40, 0.18);
}

.dq-modal-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 16px 18px;
  background: linear-gradient(
    180deg,
    rgba(248, 250, 252, 1),
    rgba(255, 255, 255, 1)
  );
}

.dq-subtitle {
  font-size: 0.85rem;
  color: rgba(17, 24, 39, 0.6);
}

.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(2, 132, 199, 0.08);
  border: 1px solid rgba(2, 132, 199, 0.12);
  color: rgba(17, 24, 39, 0.75);
  font-size: 0.82rem;
}

.dq-close-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.dq-close-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.08);
}

.dq-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(37, 99, 235, 1);
}
.dq-icon-employee {
  background: rgba(16, 185, 129, 0.12);
  color: rgba(5, 150, 105, 1);
}

/* Tabs */
.dq-tabs-wrap {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
}
.dq-tabs {
  padding: 0 12px;
}
.dq-tab {
  font-weight: 800;
  color: rgba(17, 24, 39, 0.62);
  padding: 12px 10px;
}
.dq-tab.active {
  color: rgba(17, 24, 39, 0.92);
}
.dq-tab-content {
  background: #fff;
}

/* Body */
.dq-modal-body {
  padding: 16px 18px;
  max-height: 70vh;
  overflow: auto;
}

/* Section card */
.dq-section-card {
  border-radius: 16px;
  padding: 14px;
  background: rgba(148, 163, 184, 0.12);
  border: 1px dashed rgba(148, 163, 184, 0.45);
}

.dq-section-title {
  font-weight: 900;
  color: rgba(17, 24, 39, 0.9);
}

.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
}

.dq-access-callout {
  border: 1px solid rgba(59, 130, 246, 0.18);
  background: linear-gradient(
    180deg,
    rgba(239, 246, 255, 0.95) 0%,
    rgba(248, 250, 252, 0.95) 100%
  );
  border-radius: 14px;
  padding: 14px 16px;
}
.dq-access-callout-compact {
  padding: 12px 14px;
}
.dq-access-callout-head {
  margin-bottom: 8px;
}
.dq-access-role-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.12);
  color: rgba(29, 78, 216, 1);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
}
.dq-access-role-tag {
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  color: rgba(37, 99, 235, 0.95);
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}
.dq-access-callout-text {
  margin: 0;
  color: rgba(51, 65, 85, 0.95);
  font-size: 13px;
  line-height: 1.5;
}
.dq-role-defaults {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.dq-role-default-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-wrap: wrap;
}
.dq-role-default-module {
  min-width: 92px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(51, 65, 85, 0.92);
}
.dq-role-default-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.dq-role-default-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(255, 255, 255, 0.9);
  color: rgba(71, 85, 105, 0.95);
  font-size: 11px;
  font-weight: 600;
}
.dq-perm-section-label {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(248, 250, 252, 0.95);
  color: rgba(71, 85, 105, 0.95);
  font-size: 12px;
  font-weight: 700;
}

/* Avatar */
.dq-avatar-wrap {
  flex-shrink: 0;
}
.dq-avatar {
  width: 66px;
  height: 66px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.08);
}
.dq-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.dq-avatar-icon {
  color: rgba(17, 24, 39, 0.45);
  font-size: 18px;
}

.dq-upload-btn {
  position: relative;
  overflow: hidden;
}
.dq-file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

/* Inputs */
.dq-label {
  font-weight: 800;
  color: rgba(17, 24, 39, 0.9);
  margin-bottom: 6px;
}

.dq-input-wrap,
.dq-select-wrap {
  position: relative;
}

.dq-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
  z-index: 3;
  pointer-events: none;
}

.dq-input {
  padding-left: 40px !important;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 44px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.45) !important;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
}

/* Date picker wrapper */
.dq-date-wrap {
  position: relative;
}
.dq-date {
  border-radius: 14px !important;
  min-height: 44px;
  padding-right: 40px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.dq-date-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(17, 24, 39, 0.45);
  pointer-events: none;
}

/* vue-select styling */
.dq-vselect :deep(.vs__dropdown-toggle) {
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding-left: 40px;
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.dq-vselect :deep(.vs__dropdown-toggle:focus-within) {
  border-color: rgba(59, 130, 246, 0.45);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12);
}
.dq-vselect :deep(.vs__dropdown-menu) {
  border-radius: 14px;
  padding: 6px;
  box-shadow: 0 18px 40px rgba(16, 24, 40, 0.16);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.dq-vselect :deep(.vs__selected),
.dq-vselect :deep(.vs__search) {
  margin: 0;
}

/* Sticky footer */
.dq-modal-footer {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 14px 18px;
}

.dq-btn-soft {
  border-radius: 12px;
  padding: 10px 14px;
}
.dq-btn-primary {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.18);
}

/* Permissions header */
.dq-perm-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.dq-perm-title {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-perm-badge {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.1);
  color: rgba(79, 70, 229, 1);
}

/* Switches */
.dq-perm-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.dq-switch {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.8);
}
.dq-switch input {
  display: none;
}
.dq-slider {
  width: 38px;
  height: 22px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.35);
  position: relative;
  transition: background 0.15s ease;
}
.dq-slider::after {
  content: "";
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #fff;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.15s ease;
  box-shadow: 0 6px 14px rgba(16, 24, 40, 0.14);
}
.dq-switch input:checked + .dq-slider {
  background: rgba(59, 130, 246, 0.55);
}
.dq-switch input:checked + .dq-slider::after {
  transform: translateX(16px);
}
.dq-switch-label {
  font-weight: 700;
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.7);
}

/* Permission table */
.dq-perm-table-wrap {
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.dq-perm-table td {
  vertical-align: middle;
  padding: 12px 12px;
  border-color: rgba(0, 0, 0, 0.06);
}
.dq-perm-module {
  min-width: 180px;
  font-weight: 900;
  color: rgba(17, 24, 39, 0.85);
  background: rgba(248, 250, 252, 1);
}
.dq-perm-cell {
  min-width: 120px;
}
.dq-checkline {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}
.dq-checkline-text {
  font-weight: 700;
  color: rgba(17, 24, 39, 0.75);
}
.dq-checkline-action .dq-checkline-text {
  font-weight: 700;
  color: rgba(17, 24, 39, 0.65);
}
.dq-perm-footnote {
  padding: 10px 12px;
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
  background: rgba(248, 250, 252, 1);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.dq-inline-setting {
  margin-top: 4px;
  margin-bottom: 8px;
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(248, 250, 252, 0.95);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.dq-inline-setting-title {
  font-size: 0.96rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

/* Delete modal */
.dq-delete-body {
  padding: 18px;
  text-align: center;
}
.dq-danger-hero {
  display: flex;
  justify-content: center;
  margin-top: 6px;
}
.dq-danger-badge {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.12);
  color: rgba(220, 38, 38, 1);
  font-size: 26px;
}
.dq-delete-title {
  font-weight: 900;
  letter-spacing: -0.02em;
}
.dq-delete-subtitle {
  color: rgba(17, 24, 39, 0.65);
  font-size: 0.92rem;
  line-height: 1.35rem;
}
.dq-delete-warning {
  text-align: left;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.14);
  color: rgba(120, 53, 15, 0.95);
  font-size: 0.84rem;
}
.dq-btn-danger {
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(220, 38, 38, 0.14);
}

.display-none {
  display: none !important;
}
</style>

